/**
 * WaterSystem - Handles decorative water zones
 * 
 * Creates visual water effects (Zen Spa level).
 * Water is decorative only - not deadly.
 * 
 * @module systems/WaterSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';

export default class WaterSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
        this.ripples = [];
        this.lilyPads = [];
    }

    /**
     * Create water zones from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        if (!config.waterZones) return;

        config.waterZones.forEach(w => {
            // Water surface with gradient
            const waterGraphics = this.scene.add.graphics();
            
            // Deep water base
            waterGraphics.fillStyle(0x1a5f7a, 0.7);
            waterGraphics.fillRect(w.x, w.y + 20, w.w, w.h - 20);
            
            // Surface shimmer
            waterGraphics.fillStyle(0x48a9a6, 0.5);
            waterGraphics.fillRect(w.x, w.y, w.w, 25);
            
            // Light reflection line
            waterGraphics.fillStyle(0x7fd1d1, 0.6);
            waterGraphics.fillRect(w.x, w.y, w.w, 5);
            
            // Animated ripples
            for (let i = 0; i < w.w / 100; i++) {
                this.createWaterRipple(
                    w.x + Phaser.Math.Between(30, w.w - 30), 
                    w.y + 10
                );
            }
            
            // Floating lily pads (Zen aesthetic)
            for (let i = 0; i < w.w / 200; i++) {
                this.createLilyPad(
                    w.x + Phaser.Math.Between(50, w.w - 50), 
                    w.y + Phaser.Math.Between(5, 15)
                );
            }

            this.items.push({ visual: waterGraphics, config: w });
        });

        this.isActive = this.items.length > 0;
    }

    /**
     * Create animated water ripple
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createWaterRipple(x, y) {
        const ripple = this.scene.add.circle(x, y, 5, 0x7fd1d1, 0);
        ripple.setStrokeStyle(1, 0x7fd1d1, 0.6);
        
        this.scene.tweens.add({
            targets: ripple,
            scale: 3,
            alpha: 0,
            duration: 2500,
            repeat: -1,
            delay: Phaser.Math.Between(0, 2000),
            onRepeat: () => {
                ripple.setScale(1);
                ripple.setAlpha(1);
                ripple.x = x + Phaser.Math.Between(-20, 20);
            }
        });

        this.ripples.push(ripple);
    }

    /**
     * Create floating lily pad
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createLilyPad(x, y) {
        // Lily pad - green circle with notch
        const pad = this.scene.add.graphics();
        pad.fillStyle(0x228B22, 0.8);
        pad.fillCircle(0, 0, 15);
        
        // Cut notch
        pad.fillStyle(0x1a5f7a, 0.7);
        pad.fillTriangle(-2, 0, 2, 0, 0, -15);
        
        pad.x = x;
        pad.y = y;
        
        // Gentle floating animation
        this.scene.tweens.add({
            targets: pad,
            x: x + Phaser.Math.Between(-10, 10),
            y: y + Phaser.Math.Between(-3, 3),
            rotation: Phaser.Math.FloatBetween(-0.1, 0.1),
            duration: Phaser.Math.Between(2000, 4000),
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        this.lilyPads.push(pad);
    }

    /**
     * Cleanup
     */
    destroy() {
        this.ripples.forEach(r => r.destroy());
        this.lilyPads.forEach(p => p.destroy());
        this.ripples = [];
        this.lilyPads = [];
        super.destroy();
    }
}
